import { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface OnboardingStepWrapperProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly progress: { current: number; total: number; percentage: number };
  readonly isFirstStep: boolean;
  readonly isLastStep: boolean;
  readonly isLoading?: boolean;
  readonly canProceed?: boolean;
  readonly onBack: () => void;
  readonly onNext: () => void;
  readonly onSkip: () => void;
  readonly onSkipStep: () => void;
  readonly onGoToStep?: (index: number) => void;
  readonly nextLabel?: string;
  readonly showBackButton?: boolean;
  readonly hideFooterButton?: boolean;
  readonly illustrationSrc?: string;
  /** Custom component to render in the left panel instead of the illustration image (desktop only) */
  readonly customLeftPanel?: ReactNode;
  /** Show a thin white border on the left panel container (desktop only) */
  readonly showLeftPanelBorder?: boolean;
}

function renderLeftPanelContent(
  customLeftPanel: ReactNode | undefined,
  illustrationSrc: string | undefined
): ReactNode {
  if (customLeftPanel) {
    return customLeftPanel;
  }

  if (illustrationSrc) {
    return (
      <img
        src={illustrationSrc}
        alt="Illustration"
        className="w-full h-full object-cover object-[center_30%]"
      />
    );
  }

  return (
    <div className="w-full h-full bg-white/5 flex items-center justify-center">
      <span className="text-white/20 text-sm">Illustration</span>
    </div>
  );
}

function getButtonLabel(nextLabel: string | undefined, isLastStep: boolean): string {
  if (nextLabel) {
    return nextLabel;
  }
  return isLastStep ? 'Terminer' : 'Continuer';
}

export function OnboardingStepWrapper({
  title,
  subtitle,
  children,
  progress,
  isFirstStep,
  isLastStep,
  isLoading,
  canProceed,
  onBack,
  onNext,
  onSkip,
  onSkipStep,
  onGoToStep,
  nextLabel,
  showBackButton,
  hideFooterButton,
  illustrationSrc,
  customLeftPanel,
  showLeftPanelBorder = false,
}: OnboardingStepWrapperProps) {
  return (
    <div className="fixed inset-0 bg-black flex z-[60] pointer-events-auto">
      {/* Desktop: Left column - Illustration with overlay header */}
      <div className="hidden md:flex md:w-1/2 bg-black p-4">
        <div className={cn("relative w-full h-full rounded-2xl overflow-hidden", showLeftPanelBorder && "border border-white/20")}>
          {renderLeftPanelContent(customLeftPanel, illustrationSrc)}
          
          {/* Overlay header on image - Desktop only */}
          <div className="absolute inset-x-0 top-0 p-6 bg-gradient-to-b from-black/60 via-black/30 to-transparent rounded-t-2xl">
            {/* Progress bar */}
            <div className="flex gap-1.5">
              {Array.from({ length: progress.total }).map((_, index) => {
                const stepNumber = index + 1;
                const isCompleted = stepNumber < progress.current;
                const isCurrent = stepNumber === progress.current;
                const canNavigate = isCompleted && onGoToStep;
                
                return (
                  <button
                    key={`step-${stepNumber}`}
                    type="button"
                    onClick={() => canNavigate && onGoToStep(index)}
                    disabled={!canNavigate}
                    className={cn(
                      "h-1 flex-1 rounded-full transition-colors",
                      isCurrent || isCompleted
                        ? "bg-white/60"
                        : "bg-white/[0.1]",
                      canNavigate && "hover:bg-white/80 cursor-pointer",
                      !canNavigate && "cursor-default"
                    )}
                    aria-label={canNavigate ? `Aller à l'étape ${stepNumber}` : undefined}
                  />
                );
              })}
            </div>

            {/* Step indicator */}
            <p className="text-xs text-white/40 mt-4">
              Étape {progress.current} sur {progress.total}
            </p>

            {/* Title with back button */}
            <div className="flex items-center gap-3 mt-2">
              {!isFirstStep && (
                <button
                  type="button"
                  onClick={onBack}
                  className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Retour"
                >
                  <ArrowLeft className="h-5 w-5 text-white/70" />
                </button>
              )}
              <h2 className="text-xl font-semibold text-white">{title}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Right column - Content (full width on mobile) */}
      <div className="flex-1 flex flex-col pt-[env(safe-area-inset-top)]">
        {/* Mobile only: Progress bar, step indicator, title */}
        <div className="md:hidden">
          {/* Progress bar */}
          <div className="px-6 pt-6 shrink-0">
            <div className="flex gap-1.5">
              {Array.from({ length: progress.total }).map((_, index) => {
                const stepNumber = index + 1;
                const isCompleted = stepNumber < progress.current;
                const isCurrent = stepNumber === progress.current;
                const canNavigate = isCompleted && onGoToStep;
                
                return (
                  <button
                    key={`step-${stepNumber}`}
                    type="button"
                    onClick={() => canNavigate && onGoToStep(index)}
                    disabled={!canNavigate}
                    className={cn(
                      "h-1 flex-1 rounded-full transition-colors",
                      isCurrent || isCompleted
                        ? "bg-white/60"
                        : "bg-white/[0.1]",
                      canNavigate && "hover:bg-white/80 cursor-pointer",
                      !canNavigate && "cursor-default"
                    )}
                    aria-label={canNavigate ? `Aller à l'étape ${stepNumber}` : undefined}
                  />
                );
              })}
            </div>
          </div>

          {/* Step indicator */}
          <div className="px-6 pt-4 shrink-0">
            <p className="text-xs text-white/40">
              Étape {progress.current} sur {progress.total}
            </p>
          </div>

          {/* Title & Subtitle */}
          <div className="px-6 py-3 shrink-0">
            <div className="flex items-center gap-3">
              {!isFirstStep && (
                <button
                  type="button"
                  onClick={onBack}
                  className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Retour"
                >
                  <ArrowLeft className="h-5 w-5 text-white/70" />
                </button>
              )}
              <h2 className="text-xl font-semibold text-white">{title}</h2>
            </div>
            {subtitle && (
              <p className="text-sm text-white/50 mt-1">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Desktop: Subtitle - Centered in upper half */}
          {subtitle && (
            <div className="hidden md:flex items-center justify-center px-6 py-8 shrink-0">
              <h3 className="text-xl font-semibold text-white text-center">{subtitle}</h3>
            </div>
          )}
          
          {/* Step Content - Takes remaining space; top-aligned on mobile so content doesn't go under Title & Subtitle */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 flex md:items-center md:justify-center items-start justify-center">
            {children}
          </div>
        </div>

        {/* Footer */}
        {!hideFooterButton && (
          <div className="px-6 pt-4 pb-8 border-t border-white/[0.04] shrink-0 mb-[env(safe-area-inset-bottom)]">
            <div className="flex flex-col items-center gap-3">
              <Button
                onClick={onNext}
                disabled={isLoading || !canProceed}
                className="w-full h-12 bg-white/10 hover:bg-white/20 text-white/70 border border-white/10 rounded-xl font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    {' '}
                    Chargement...
                  </span>
                ) : (
                  getButtonLabel(nextLabel, isLastStep)
                )}
              </Button>
              
              <button
                type="button"
                onClick={onSkipStep}
                className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors"
              >
                Passer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
